"use client";

import { useState, useEffect } from "react";
import { Loader2, Plus, X, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getTodayLocalDate } from "@/lib/format/date";

interface Area {
  id: string;
  name: string;
}

interface TimeWindow {
  start: string;
  end: string;
}

interface PreviewResult {
  preview: boolean;
  totalGenerated: number;
  duplicatesSkipped: number;
  newSlotsCount: number;
}

interface SlotGeneratorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  areas: Area[];
  onSuccess: () => void;
  defaultAreaId?: string;
}

const SLOT_TYPES = [
  { value: "MEASUREMENT", label: "Measurement" },
  { value: "PICKUP", label: "Pickup" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "RETURN", label: "Return" },
  { value: "REWORK_PICKUP", label: "Rework Pickup" },
];

const DAYS_OF_WEEK = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const DURATION_OPTIONS = [
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1.5 hours" },
  { value: 120, label: "2 hours" },
];

export function SlotGeneratorDialog({
  open,
  onOpenChange,
  areas,
  onSuccess,
  defaultAreaId,
}: SlotGeneratorDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  const [areaId, setAreaId] = useState("");
  const [slotType, setSlotType] = useState("MEASUREMENT");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]);
  const [timeWindows, setTimeWindows] = useState<TimeWindow[]>([
    { start: "09:00", end: "13:00" },
    { start: "14:00", end: "18:00" },
  ]);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [capacity, setCapacity] = useState("5");

  useEffect(() => {
    const today = getTodayLocalDate();
    setAreaId(defaultAreaId || (areas.length > 0 ? areas[0].id : ""));
    setSlotType("MEASUREMENT");
    setStartDate(today);
    setEndDate(today);
    setDaysOfWeek([1, 2, 3, 4, 5]);
    setTimeWindows([
      { start: "09:00", end: "13:00" },
      { start: "14:00", end: "18:00" },
    ]);
    setDurationMinutes(60);
    setCapacity("5");
    setError(null);
    setPreview(null);
  }, [open, areas, defaultAreaId]);

  const toggleDay = (day: number) => {
    setDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
    setPreview(null);
  };

  const addTimeWindow = () => {
    setTimeWindows([...timeWindows, { start: "09:00", end: "10:00" }]);
    setPreview(null);
  };

  const removeTimeWindow = (index: number) => {
    if (timeWindows.length > 1) {
      setTimeWindows(timeWindows.filter((_, i) => i !== index));
      setPreview(null);
    }
  };

  const updateTimeWindow = (
    index: number,
    field: "start" | "end",
    value: string
  ) => {
    const updated = [...timeWindows];
    updated[index][field] = value;
    setTimeWindows(updated);
    setPreview(null);
  };

  const getPayload = () => ({
    areaId,
    slotType,
    startDate,
    endDate,
    daysOfWeek,
    timeWindows,
    durationMinutes,
    capacity: parseInt(capacity),
  });

  const handlePreview = async () => {
    setError(null);
    setIsPreviewing(true);
    setPreview(null);

    try {
      const res = await fetch("/api/admin/slots/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...getPayload(), preview: true }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to preview slots");
        return;
      }

      setPreview(data);
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const res = await fetch("/api/admin/slots/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(getPayload()),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to generate slots");
        return;
      }

      onSuccess();
      onOpenChange(false);
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-teal-600" />
            Slot Generator
          </DialogTitle>
          <DialogDescription>
            Bulk create time slots over a date range with customizable rules.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Area</Label>
              <Select value={areaId} onValueChange={(v) => { setAreaId(v); setPreview(null); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent>
                  {areas.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      {area.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Slot Type</Label>
              <Select value={slotType} onValueChange={(v) => { setSlotType(v); setPreview(null); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {SLOT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPreview(null); }}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPreview(null); }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Days of Week</Label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <label
                  key={day.value}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={daysOfWeek.includes(day.value)}
                    onCheckedChange={() => toggleDay(day.value)}
                  />
                  <span className="text-sm">{day.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Time Windows</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTimeWindow}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Window
              </Button>
            </div>
            <div className="space-y-2">
              {timeWindows.map((window, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={window.start}
                    onChange={(e) =>
                      updateTimeWindow(index, "start", e.target.value)
                    }
                    className="flex-1"
                  />
                  <span className="text-slate-400">to</span>
                  <Input
                    type="time"
                    value={window.end}
                    onChange={(e) =>
                      updateTimeWindow(index, "end", e.target.value)
                    }
                    className="flex-1"
                  />
                  {timeWindows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTimeWindow(index)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Slot Duration</Label>
              <Select
                value={durationMinutes.toString()}
                onValueChange={(v) => { setDurationMinutes(parseInt(v)); setPreview(null); }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Capacity per Slot</Label>
              <Input
                type="number"
                min="1"
                value={capacity}
                onChange={(e) => { setCapacity(e.target.value); setPreview(null); }}
              />
            </div>
          </div>

          {preview && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-2">Preview</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total slots to generate:</span>
                  <span className="font-medium">{preview.totalGenerated}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Duplicates (will skip):</span>
                  <span className="font-medium text-amber-600">
                    {preview.duplicatesSkipped}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">New slots to create:</span>
                  <span className="font-medium text-teal-600">
                    {preview.newSlotsCount}
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting || isPreviewing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handlePreview}
            disabled={
              isSubmitting ||
              isPreviewing ||
              !areaId ||
              !slotType ||
              daysOfWeek.length === 0
            }
          >
            {isPreviewing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Previewing...
              </>
            ) : (
              "Preview"
            )}
          </Button>
          <Button
            type="button"
            onClick={handleGenerate}
            disabled={
              isSubmitting ||
              isPreviewing ||
              !areaId ||
              !slotType ||
              daysOfWeek.length === 0 ||
              (preview !== null && preview.newSlotsCount === 0)
            }
            className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="mr-2 h-4 w-4" />
                Generate Slots
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
